import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card, Row, Col } from "react-bootstrap";
import { TextField, Tooltip, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  IoRefresh,
  IoChevronDown,
  IoChevronForward,
  IoCopy,
} from "react-icons/io5";
import { ethers } from "ethers";
import toast from "react-hot-toast";

import config from "../../../../configs";
import walletMultisigArtifact from "../../../../configs/abis/wallet-multisig.json";
import Pagination from "../../Live/components/Pagenation";
import { request } from "../../../../admin/plugins/axios";
import { endpoints } from "../../../../utils/networks";

const { abi: walletAbi } = walletMultisigArtifact;
const walletInterface = new ethers.utils.Interface(walletAbi);
const PAGE_SIZE = 5;

const shorten = (value) =>
  value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "--";

const formatBnb = (value) => {
  if (value === null || typeof value === "undefined") return "--";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${num.toLocaleString(undefined, {
    maximumFractionDigits: 4,
  })} BNB`;
};

// Component to render address with tooltip and copy button
const AddressWithTooltip = ({
  shortenedAddress,
  fullAddress,
  addressName,
  onCopy,
}) => {
  const handleCopy = (e) => {
    e.stopPropagation();
    if (fullAddress && onCopy) {
      onCopy(fullAddress);
    }
  };

  return (
    <Tooltip
      title={
        <div
          style={{
            padding: "4px 0",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minWidth: "200px",
          }}
        >
          {addressName && (
            <div
              style={{
                fontSize: "0.95rem",
                fontWeight: 500,
                color: "#fff",
                marginBottom: "4px",
              }}
            >
              {addressName}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                color: "#bfc3d9",
                fontFamily: "monospace",
                wordBreak: "break-all",
              }}
            >
              {fullAddress || shortenedAddress}
            </span>
            {fullAddress && (
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  color: "#fff",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </div>
        </div>
      }
      placement="top"
      arrow
    >
      <span
        style={{
          cursor: "pointer",
          textDecoration: "underline",
          textDecorationStyle: "dotted",
          color: "#fff",
        }}
      >
        {shortenedAddress}
      </span>
    </Tooltip>
  );
};

// Render details text with interactive addresses
const renderDetailsText = (text, addresses, addressNames, onCopy) => {
  if (!text || text === "--") return text;

  if (!addresses || addresses.length === 0) {
    return text;
  }

  // Create a map of shortened -> full address
  const addressMap = {};
  addresses.forEach(({ full, short }) => {
    addressMap[short] = full;
  });

  // Split text and replace addresses with interactive components
  const parts = [];
  let lastIndex = 0;
  const addressPattern = /0x[a-fA-F0-9]{4,6}\.\.\.[a-fA-F0-9]{4}/g;
  let match;

  while ((match = addressPattern.exec(text)) !== null) {
    // Add text before the address
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the interactive address component
    const shortened = match[0];
    const fullAddress = addressMap[shortened];
    const lowerAddr = fullAddress?.toLowerCase();
    const addressName = lowerAddr ? addressNames[lowerAddr] : null;

    parts.push(
      <AddressWithTooltip
        key={`addr-${match.index}`}
        shortenedAddress={shortened}
        fullAddress={fullAddress}
        addressName={addressName}
        onCopy={onCopy}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

// Parse withdraw details and return both text and addresses
const parseWithdrawDetailsWithAddresses = (functionName, data) => {
  if (!data || data === "0x") return { text: "--", addresses: [] };

  const addresses = [];
  let text = parseWithdrawDetails(functionName, data);

  if (text === "--" || text === "Unable to decode details") {
    return { text, addresses: [] };
  }

  // Extract addresses from decoded data
  try {
    const normalizedName = functionName?.replace?.(/^_/, "") || "";
    let fragment;
    try {
      fragment = walletInterface.getFunction(functionName);
    } catch (inner) {
      fragment = walletInterface.getFunction(normalizedName);
    }
    const decoded = walletInterface.decodeFunctionData(fragment, data);

    if (normalizedName === "requestWithdraw") {
      const to = decoded[0];
      const toAddr = typeof to === "string" ? to : to?.toString?.() || to;
      if (toAddr && toAddr !== ethers.constants.AddressZero) {
        addresses.push({ full: toAddr, short: shorten(toAddr) });
      }
    } else {
      // Extract all address parameters
      fragment.inputs.forEach((input, index) => {
        if (input.type === "address" && decoded[index]) {
          const addr =
            typeof decoded[index] === "string"
              ? decoded[index]
              : decoded[index]?.toString?.() || decoded[index];
          if (addr && addr !== ethers.constants.AddressZero) {
            addresses.push({ full: addr, short: shorten(addr) });
          }
        }
      });
    }
  } catch (error) {
    try {
      const fallback = ethers.utils.defaultAbiCoder.decode(
        ["address", "uint256"],
        data
      );
      const to = fallback[0];
      const toAddr = typeof to === "string" ? to : to?.toString?.() || to;
      if (toAddr && toAddr !== ethers.constants.AddressZero) {
        addresses.push({ full: toAddr, short: shorten(toAddr) });
      }
    } catch (inner) {
      // Ignore errors
    }
  }

  return { text, addresses };
};

const parseWithdrawDetails = (functionName, data) => {
  if (!data || data === "0x") return "--";
  const normalizedName = functionName?.replace?.(/^_/, "") || "";
  try {
    let fragment;
    try {
      fragment = walletInterface.getFunction(functionName);
    } catch (inner) {
      fragment = walletInterface.getFunction(normalizedName);
    }
    const decoded = walletInterface.decodeFunctionData(fragment, data);
    if (normalizedName === "requestWithdraw") {
      const to = decoded[0];
      const amount = decoded[1];
      const formatted = ethers.utils.formatEther(amount || 0);
      return `Withdraw ${formatted} BNB to ${shorten(to)}`;
    }
    const entries = fragment.inputs.map((input, index) => {
      const value = decoded[index];
      let display;
      if (ethers.BigNumber.isBigNumber(value)) {
        display = value.toString();
      } else if (typeof value === "string") {
        display = value;
      } else if (value?.toString) {
        display = value.toString();
      } else {
        display = JSON.stringify(value);
      }
      return `${input.name || `arg${index}`}: ${display}`;
    });
    return entries.join(" | ");
  } catch (error) {
    try {
      const fallback = ethers.utils.defaultAbiCoder.decode(
        ["address", "uint256"],
        data
      );
      const to = fallback[0];
      const amount = ethers.utils.formatEther(fallback[1] || 0);
      return `Withdraw ${amount} BNB to ${shorten(to)}`;
    } catch (inner) {
      console.warn("Unable to decode withdraw details", {
        functionName,
        data,
        error: inner,
      });
      return "Unable to decode details";
    }
  }
};

function WalletMultisigSection({
  provider,
  signer,
  currentUser,
  adminAddresses = [],
  refreshKey,
  onGlobalRefresh,
}) {
  const walletAddress = config.get("smartContract.WALLET_MULTISIG_ADDRESS");
  const [owners, setOwners] = useState([]);
  const [balance, setBalance] = useState(null);
  const [balanceWei, setBalanceWei] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [requiredConfirmations, setRequiredConfirmations] = useState(3);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [addressNames, setAddressNames] = useState({});
  const addressNamesRef = useRef({});
  const [activeTab, setActiveTab] = useState("all"); // "all", "pending", or "executed"

  const normalizedOwners = useMemo(() => {
    const base = owners?.length ? owners : adminAddresses;
    return base
      .filter(Boolean)
      .map((addr) => addr.toLowerCase())
      .filter((addr, idx, arr) => arr.indexOf(addr) === idx);
  }, [owners, adminAddresses]);

  const currentUserLower = currentUser?.toLowerCase();
  const isOwner =
    currentUserLower &&
    normalizedOwners.some((addr) => addr === currentUserLower);

  const fetchAddressNames = useCallback(async (addresses = []) => {
    if (!addresses.length) return;
    const normalized = Array.from(
      new Set(
        addresses
          .map((addr) => (typeof addr === "string" ? addr.toLowerCase() : null))
          .filter((addr) => addr && !addressNamesRef.current[addr])
      )
    );
    if (!normalized.length) return;
    try {
      const response = await request(
        endpoints.Users.GetNames.method,
        endpoints.Users.GetNames.url,
        { addresses: normalized }
      );
      const users = response?.data?.data?.users || response?.data?.users || [];
      const mapping = {};
      users.forEach((user) => {
        const lower = user?.wallet_address?.toLowerCase?.();
        if (lower) {
          mapping[lower] = user?.name || user?.username || user?.wallet_address;
        }
      });
      if (Object.keys(mapping).length) {
        addressNamesRef.current = { ...addressNamesRef.current, ...mapping };
        setAddressNames((prev) => ({ ...prev, ...mapping }));
      }
    } catch (error) {
      console.error("Failed to fetch user names", error);
    }
  }, []);

  const handleCopy = useCallback((value) => {
    if (!value) return;
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Failed to copy"));
  }, []);

  const handleCopyAddress = useCallback((address) => {
    if (!address) return;
    navigator.clipboard
      .writeText(address)
      .then(() => toast.success("Address copied to clipboard"))
      .catch(() => toast.error("Failed to copy address"));
  }, []);

  const fetchOwners = useCallback(async () => {
    if (!provider || !walletAddress) return;
    try {
      const contract = new ethers.Contract(walletAddress, walletAbi, provider);
      const ownersList = await contract.getOwners();
      const required = await contract.numConfirmationsRequired();
      setOwners(ownersList || []);
      setRequiredConfirmations(Number(required?.toString?.() || 3));
    } catch (error) {
      console.error("Failed to load wallet multisig owners", error);
    }
  }, [provider, walletAddress]);

  const fetchBalance = useCallback(async () => {
    if (!provider || !walletAddress) return;
    setBalanceLoading(true);
    try {
      const contract = new ethers.Contract(walletAddress, walletAbi, provider);
      const rawBalance = await contract.getBalance();
      setBalanceWei(rawBalance);
      setBalance(ethers.utils.formatEther(rawBalance || 0));
    } catch (error) {
      console.error("Failed to load wallet balance", error);
      toast.error("Unable to load multisig wallet balance");
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, [provider, walletAddress]);

  const fetchTransactions = useCallback(async () => {
    if (!provider || !walletAddress || !signer || !isOwner) {
      setRows([]);
      setTotal(0);
      setTableLoading(false);
      return;
    }
    setTableLoading(true);
    try {
      const contract = new ethers.Contract(walletAddress, walletAbi, signer);
      const contractPage = Math.max(page - 1, 0);
      const [txs, totalList] = await contract.getAllTransactions(
        ethers.BigNumber.from(contractPage),
        ethers.BigNumber.from(PAGE_SIZE)
      );
      const serialized = txs.map((tx, idx) => {
        const numConfirmations = Number(tx.numConfirmations || 0);
        return {
          from: tx.from,
          executed: tx.executed,
          numConfirmations,
          functionName: tx.functionName,
          data: tx.data,
          confirmed: tx.confirmed || [],
          createdAt: Number(tx.createdAt || 0),
          updatedAt: Number(tx.updatedAt || 0),
          transactionIndex: idx,
        };
      });
      setRows(serialized);
      setTotal(Number(totalList || 0));
    } catch (error) {
      console.error("Failed to load wallet multisig transactions", error);
      toast.error("Unable to load wallet multisig transactions");
      setRows([]);
      setTotal(0);
    } finally {
      setTableLoading(false);
    }
  }, [provider, walletAddress, signer, isOwner, page]);

  useEffect(() => {
    setPage(1);
  }, [refreshKey, walletAddress]);

  useEffect(() => {
    fetchOwners();
    fetchBalance();
  }, [fetchOwners, fetchBalance, refreshKey]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions, refreshKey, localRefresh]);

  const offset = (page - 1) * PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));

  const rowsWithMeta = rows.map((row, index) => {
    const transactionId = offset + index + 1;
    const approvals = row.confirmed.length;
    const executed =
      row.executed && row.executed !== ethers.constants.AddressZero;
    const hasApproved =
      currentUserLower &&
      row.confirmed.some(
        (addr) => addr && addr.toLowerCase() === currentUserLower
      );

    if (row.confirmed && row.confirmed.length > 0) {
      fetchAddressNames(row.confirmed);
    }

    // Fetch name for the requester (row.from)
    if (row.from) {
      fetchAddressNames([row.from]);
    }

    // Extract addresses from details for fetching names
    const { text, addresses } = parseWithdrawDetailsWithAddresses(
      row.functionName,
      row.data
    );
    const detailAddresses = addresses.map(({ full }) => full).filter(Boolean);
    if (detailAddresses.length > 0) {
      fetchAddressNames(detailAddresses);
    }

    return {
      ...row,
      transactionId,
      approvals,
      numConfirmations: requiredConfirmations || 0,
      executed,
      hasApproved,
      canApprove: isOwner && !executed && !hasApproved,
      canExecute:
        isOwner &&
        !executed &&
        requiredConfirmations > 0 &&
        approvals >= requiredConfirmations,
      detailsText: text,
      detailsAddresses: addresses,
    };
  });

  // Filter rows based on active tab
  const filteredRows = useMemo(() => {
    if (activeTab === "executed") {
      return rowsWithMeta.filter((row) => row.executed);
    } else if (activeTab === "pending") {
      // pending - show non-executed transactions
      return rowsWithMeta.filter((row) => !row.executed);
    } else {
      // all - show all transactions
      return rowsWithMeta;
    }
  }, [rowsWithMeta, activeTab]);

  const handleRequestWithdraw = async (event) => {
    event.preventDefault();
    if (!isOwner) {
      toast.error("You are not allowed to request withdraw");
      return;
    }
    if (!ethers.utils.isAddress(recipient)) {
      toast.error("Invalid recipient address");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error("Amount must be greater than zero");
      return;
    }
    if (!signer) {
      toast.error("Connect your wallet to submit");
      return;
    }
    const parsedAmount = ethers.utils.parseEther(String(amount));
    if (!balanceWei || parsedAmount.gt(balanceWei)) {
      toast.error("Insufficient contract balance");
      return;
    }

    try {
      setRequestLoading(true);
      const contract = new ethers.Contract(walletAddress, walletAbi, signer);
      const tx = await contract.requestWithdraw(recipient, parsedAmount);
      toast.loading("Submitting withdraw request...", {
        id: "wallet-withdraw",
      });
      const receipt = await tx.wait();
      const transactionId = receipt?.events?.find(
        (e) => e.event === "WithdrawRequested"
      )?.args?.txId;
      toast.success(
        transactionId
          ? `Withdraw request queued (Tx #${transactionId}).`
          : "Withdraw request queued.",
        { id: "wallet-withdraw" }
      );
      setRecipient("");
      setAmount("");
      setLocalRefresh((prev) => prev + 1);
      onGlobalRefresh?.();
      fetchBalance();
    } catch (error) {
      console.error("Withdraw request failed", error);
      toast.error(
        error?.reason || error?.message || "Withdraw request failed",
        {
          id: "wallet-withdraw",
        }
      );
    } finally {
      setRequestLoading(false);
    }
  };

  const handleConfirm = async (transactionId) => {
    if (!signer) {
      toast.error("Connect your wallet to approve");
      return;
    }
    setActionId(transactionId);
    try {
      const contract = new ethers.Contract(walletAddress, walletAbi, signer);
      const tx = await contract.confirmTransaction(transactionId);
      toast.loading("Submitting approval...", { id: "wallet-multi-action" });
      await tx.wait();
      toast.success("Approval recorded", { id: "wallet-multi-action" });
      setLocalRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Approval failed", error);
      toast.error(error?.reason || error?.message || "Approval failed", {
        id: "wallet-multi-action",
      });
    } finally {
      setActionId(null);
    }
  };

  const handleExecute = async (transactionId) => {
    if (!signer) {
      toast.error("Connect your wallet to execute");
      return;
    }
    setActionId(transactionId);
    try {
      const contract = new ethers.Contract(walletAddress, walletAbi, signer);
      const tx = await contract.executeTransaction(transactionId);
      toast.loading("Executing transaction...", { id: "wallet-multi-action" });
      await tx.wait();
      toast.success("Transaction executed", { id: "wallet-multi-action" });
      setLocalRefresh((prev) => prev + 1);
      onGlobalRefresh?.();
      fetchBalance();
    } catch (error) {
      console.error("Execute failed", error);
      toast.error(error?.reason || error?.message || "Execution failed", {
        id: "wallet-multi-action",
      });
    } finally {
      setActionId(null);
    }
  };

  const renderBody = () => {
    if (!provider || !walletAddress) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            Connect to the correct network to view multisig transactions.
          </td>
        </tr>
      );
    }
    if (!isOwner || !signer) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            Connect with an authorized owner wallet to view multisig activity.
          </td>
        </tr>
      );
    }
    if (tableLoading) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            Loading wallet multisig activity...
          </td>
        </tr>
      );
    }
    if (!filteredRows.length) {
      return (
        <tr>
          <td className="table-no-data" colSpan={7}>
            {activeTab === "executed"
              ? "No executed transactions found."
              : activeTab === "pending"
              ? "No pending transactions found."
              : "No transactions found."}
          </td>
        </tr>
      );
    }

    return filteredRows.map((row) => (
      <tr key={row.transactionId}>
        <td>{row.transactionId}</td>
        <td>{row.functionName || "--"}</td>
        <td>
          {renderDetailsText(
            row.detailsText || parseWithdrawDetails(row.functionName, row.data),
            row.detailsAddresses || [],
            addressNames,
            handleCopyAddress
          )}
        </td>
        <td>
          {(() => {
            const fromLower = row.from?.toLowerCase?.();
            const fromName = fromLower ? addressNames[fromLower] : null;
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Tooltip
                  title={
                    <div
                      style={{
                        padding: "4px 0",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        minWidth: "200px",
                      }}
                    >
                      {fromName && (
                        <div
                          style={{
                            fontSize: "0.95rem",
                            fontWeight: 500,
                            color: "#fff",
                            marginBottom: "4px",
                          }}
                        >
                          {fromName}
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.85rem",
                            color: "#bfc3d9",
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                          }}
                        >
                          {row.from || "--"}
                        </span>
                        {row.from && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyAddress(row.from);
                            }}
                            sx={{
                              color: "#fff",
                              padding: "4px",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                              },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  }
                  placement="top"
                  arrow
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      lineHeight: 1.1,
                      cursor: "pointer",
                    }}
                  >
                    {fromName && (
                      <span
                        style={{
                          fontSize: "1rem",
                          color: "#bfc3d9",
                          marginBottom: 4,
                        }}
                      >
                        {fromName}
                      </span>
                    )}
                    <span style={{ fontSize: "0.75rem" }}>
                      {shorten(row.from)}
                    </span>
                  </div>
                </Tooltip>
              </div>
            );
          })()}
        </td>
        <td>
          <Tooltip
            title={
              row.confirmed && row.confirmed.length > 0 ? (
                <div style={{ padding: "8px" }}>
                  <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
                    Confirmed by ({row.confirmed.length}):
                  </div>
                  {row.confirmed.map((addr, idx) => {
                    const lowerAddr = addr?.toLowerCase?.();
                    const name = lowerAddr ? addressNames[lowerAddr] : null;
                    return (
                      <div
                        key={idx}
                        style={{
                          marginBottom: "4px",
                          fontSize: "0.85rem",
                          lineHeight: "1.4",
                        }}
                      >
                        {name && (
                          <div style={{ fontWeight: "500", color: "#bfc3d9" }}>
                            {name}
                          </div>
                        )}
                        <div style={{ fontSize: "0.75rem", color: "#fff" }}>
                          {shorten(addr)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                "No confirmations yet"
              )
            }
            placement="top"
            arrow
          >
            <span style={{ cursor: "help" }}>
              {`${row.approvals}/${
                row.numConfirmations || requiredConfirmations
              }`}
            </span>
          </Tooltip>
        </td>
        <td>
          {row.executed
            ? "Executed"
            : row.approvals >= (row.numConfirmations || requiredConfirmations)
            ? "Ready"
            : "Pending"}
        </td>
        <td>
          {row.executed ? (
            <span className="text-muted" style={{ fontStyle: "italic" }}>
              Executed
            </span>
          ) : (
            <div className="d-flex gap-2 flex-wrap">
              {row.hasApproved ? (
                <button
                  type="button"
                  className="transaction-filter-btn"
                  disabled
                  style={{ opacity: 0.6, cursor: "not-allowed" }}
                >
                  Approved
                </button>
              ) : (
                <button
                  type="button"
                  className="transaction-filter-btn"
                  disabled={!row.canApprove || actionId === row.transactionId}
                  onClick={() => handleConfirm(row.transactionId)}
                >
                  {actionId === row.transactionId ? "Processing..." : "Approve"}
                </button>
              )}
              {row.canExecute && (
                <button
                  type="button"
                  className="transaction-filter-btn"
                  disabled={actionId === row.transactionId}
                  onClick={() => handleExecute(row.transactionId)}
                  style={{
                    borderColor: "#f8780c",
                    background: "rgba(248, 120, 12, 0.1)",
                    color: "#f8780c",
                  }}
                >
                  {actionId === row.transactionId ? "Processing..." : "Execute"}
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <>
      <Card className="bg-live mb-4 p-2">
        <Card.Body>
          <div className="contract-summary-panel-head">
            <div className="d-flex align-items-center justify-space-between w-100">
              <div className="d-flex align-items-center gap-3">
                <h5 className="mb-0 min-w-250px">
                  Wallet Transactions (Multisig)
                </h5>
                <div
                  className="transactions-filter-buttons gap-1"
                  style={{ marginBottom: 0 }}
                >
                  <button
                    type="button"
                    className={`transaction-filter-btn2 ${
                      activeTab === "all" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("all")}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`transaction-filter-btn2 ${
                      activeTab === "pending" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("pending")}
                  >
                    Pending
                  </button>
                  <button
                    type="button"
                    className={`transaction-filter-btn2 ${
                      activeTab === "executed" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("executed")}
                  >
                    Executed
                  </button>
                </div>
              </div>
              <small style={{ display: "flex", gap: "15px" }}>
                <div
                  className="d-flex flex-column flex-sm-row align-items-sm-center gap-3 "
                  style={{
                    borderRight: "1px solid rgb(238 238 238 / 13%)",
                    paddingRight: "15px",
                  }}
                >
                  <div className="summary-address-wrapper">
                    <span className="summary-label mb-0">Master Wallet</span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="summary-address">
                        {shorten(walletAddress)}
                      </span>
                      <button
                        type="button"
                        className="summary-copy-btn"
                        onClick={() => handleCopy(walletAddress)}
                      >
                        <IoCopy />
                      </button>
                    </div>
                  </div>
                  <div className="text-left flex align-items-center gap-2">
                    <p className="summary-label mb-0">Balance</p>
                    <div className="d-flex align-items-center gap-2">
                      <strong>
                        {balanceLoading ? "Loading..." : formatBnb(balance)}
                      </strong>
                      <button
                        type="button"
                        className="summary-copy-btn"
                        onClick={() => fetchBalance()}
                        title="Refresh balance"
                      >
                        <IoRefresh />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="summary-copy-btn"
                    onClick={() => setLocalRefresh((prev) => prev + 1)}
                  >
                    <IoRefresh />
                  </button>
                  {isOwner && (
                    <button
                      type="button"
                      className="summary-copy-btn1"
                      onClick={() => setShowRequestForm((prev) => !prev)}
                    >
                      <span className="mr-3">
                        {showRequestForm ? "Hide Request" : "Withdraw"}
                      </span>
                      {showRequestForm ? (
                        <IoChevronDown />
                      ) : (
                        <IoChevronForward />
                      )}
                    </button>
                  )}
                </div>
              </small>
            </div>
            <div className="borderSpace borderSpaceTitle title-inbox mt-4 "></div>
          </div>
          {showRequestForm && isOwner && (
            <div className="mt-3">
              <Card
                className="bg-live p-2 mb-4 h-100"
                style={{
                  background:
                    "linear-gradient(96.27deg, #eb6a1b12 12.79%, #ff00e20f 50.13%, #28245f0f 107.19%)",
                }}
              >
                <Card.Body>
                  <div className="d-flex align-items-center justify-space-between w-100">
                    <h6 className="mb-3">Request Withdraw</h6>
                  </div>
                  <form onSubmit={handleRequestWithdraw} className="mint-form1">
                    <Row className="g-3 w-100 align-items-end">
                      <Col xs={12} md={6} lg={5}>
                        <TextField
                          label="Recipient address"
                          variant="outlined"
                          value={recipient}
                          onChange={(e) => setRecipient(e.target.value)}
                          fullWidth
                          required
                          InputProps={{ className: "mint-input" }}
                          InputLabelProps={{ className: "mint-input" }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              color: "#ffffff",
                              backgroundColor: "rgba(255, 255, 255, 0.05)",
                              borderRadius: "999px",
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                                borderRadius: "999px",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.5)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#f8780c",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.7)",
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#f8780c",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "#ffffff",
                            },
                            "& .MuiOutlinedInput-input::placeholder": {
                              color: "rgba(255, 255, 255, 0.5)",
                              opacity: 1,
                            },
                          }}
                        />
                      </Col>
                      <Col xs={12} md={4} lg={4}>
                        <TextField
                          label="Amount (BNB)"
                          variant="outlined"
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          fullWidth
                          required
                          InputProps={{ className: "mint-input" }}
                          InputLabelProps={{ className: "mint-input" }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              color: "#ffffff",
                              backgroundColor: "rgba(255, 255, 255, 0.05)",
                              borderRadius: "999px",
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                                borderRadius: "999px",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.5)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#f8780c",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: "rgba(255, 255, 255, 0.7)",
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#f8780c",
                            },
                            "& .MuiOutlinedInput-input": {
                              color: "#ffffff",
                            },
                            "& .MuiOutlinedInput-input::placeholder": {
                              color: "rgba(255, 255, 255, 0.5)",
                              opacity: 1,
                            },
                          }}
                        />
                      </Col>
                      <Col xs={12} md={2} lg={3} className="d-flex">
                        <button
                          type="submit"
                          className={`presale-submit-btn flex-grow-1 ${
                            requestLoading ? "disabled" : ""
                          }`}
                          disabled={requestLoading}
                          style={{ width: "100%" }}
                        >
                          {requestLoading
                            ? "Processing..."
                            : "Request Withdraw"}
                        </button>
                      </Col>
                    </Row>
                    {!isOwner && (
                      <p className="text-center mt-2 text-warning">
                        Connect with the authorized wallet to request withdraw.
                      </p>
                    )}
                  </form>
                </Card.Body>
              </Card>
            </div>
          )}
          <div className="table-responsive-wrapper mt-3">
            <table className="purchasing-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Function</th>
                  <th>Details</th>
                  <th>Requested By</th>
                  <th>Confirmations</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>{renderBody()}</tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      {total > 0 && (
        <Pagination
          totalList={total}
          pages={totalPages}
          currentPage={page}
          setCurrentPage={setPage}
          itemsPerPage={PAGE_SIZE}
        />
      )}
    </>
  );
}

export default WalletMultisigSection;
